import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Gauge, Settings, CircleDot, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import { isCallForPrice } from '../utils/priceDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VehicleCard = ({ vehicle }) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const images = vehicle.images || [];
  
  const fallbackImage = '/photos-coming-soon.jpg';
  
  const getImgUrl = (url) => {
    if (!url) return fallbackImage;
    return resolveImageUrl(url) || fallbackImage;
  };

  const handleImageError = () => {
    if (currentImgIndex < images.length - 1) {
      setCurrentImgIndex(prev => prev + 1);
    } else {
      setImageError(true);
    }
  };

  const imageSrc = imageError || !images[currentImgIndex] ? fallbackImage : getImgUrl(images[currentImgIndex]);
  
  // Badge color mapping
  const getBadgeColor = (badge) => {
    const colors = {
      'Low km': 'bg-green-600',
      'Price Drop': 'bg-red-600',
      'Hot Deal': 'bg-red-600',
      'Just Arrived': 'bg-gray-900',
      'Certified': 'bg-gray-900',
      'One Owner': 'bg-gray-700',
      'No Accidents': 'bg-green-600',
    };
    return colors[badge] || 'bg-gray-600';
  };

  // Condition badge color
  const getConditionColor = (condition) => {
    const colors = {
      'New': 'bg-green-600',
      'Used': 'bg-gray-900',
      'Demo': 'bg-gray-700',
      'Incoming': 'bg-red-600',
    };
    return colors[condition] || 'bg-gray-600';
  };
  
  return (
    <Link to={`/vehicle/${vehicle.id}`} className="block h-full">
      <div className="bg-white rounded-2xl overflow-hidden ring-1 ring-gray-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group h-full flex flex-col">
        {/* Image container */}
        <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
          <img
            src={imageSrc}
            alt={vehicle.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={handleImageError}
            loading="lazy"
          />
          {/* Badges */}
          <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap max-w-[80%]">
            {vehicle.condition && (
              <Badge className={`${getConditionColor(vehicle.condition)} text-white text-[9px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 sm:py-1`}>
                {vehicle.condition === 'Incoming' ? 'Incoming Unit' : vehicle.condition}
              </Badge>
            )}
            {vehicle.badges && vehicle.badges.map((badge, index) => (
              <Badge
                key={index}
                className={`${getBadgeColor(badge)} text-white text-[9px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 sm:py-1`}
              >
                {badge}
              </Badge>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 flex flex-col flex-grow">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-0.5 sm:mb-1 line-clamp-2 group-hover:text-red-600 transition-colors min-h-[2rem] sm:min-h-[2.5rem]">
            {vehicle.title}{vehicle.trim && !vehicle.title?.includes(vehicle.trim) ? ` ${vehicle.trim}` : ''}
          </h3>
          
          <p className="text-base sm:text-xl font-bold text-red-600 mb-2 sm:mb-3">
            {isCallForPrice(vehicle) ? 'Call for price' : `$${Number(vehicle.price).toLocaleString()}`}
            {!isCallForPrice(vehicle) && (
              <span className="text-[9px] sm:text-xs font-normal text-gray-500 ml-1 block">excl. taxes</span>
            )}
          </p>

          {/* Specs */}
          <div className="flex items-center justify-between text-[10px] sm:text-sm text-gray-600 mb-2 sm:mb-3 pb-2 sm:pb-3 border-b">
            <div className="flex items-center gap-1">
              <Gauge className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
              <span>{(vehicle.km || 0).toLocaleString()} km</span>
            </div>
            {vehicle.transmission && (
              <div className="items-center gap-1 hidden sm:flex">
                <Settings size={14} className="text-gray-400" />
                <span>{vehicle.transmission}</span>
              </div>
            )}
            {vehicle.drivetrain && (
              <div className="items-center gap-1 hidden sm:flex">
                <CircleDot size={14} className="text-gray-400" />
                <span>{vehicle.drivetrain}</span>
              </div>
            )}
          </div>

          {/* Button */}
          <div className="mt-auto w-full bg-black hover:bg-red-600 text-white rounded-lg py-1.5 sm:py-2 font-medium text-xs sm:text-sm transition-all duration-300 flex items-center justify-center gap-1.5 group/btn">
            View Details
            <ArrowRight size={14} className="transition-transform group-hover/btn:translate-x-1 hidden sm:block" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const VehicleInventory = () => {
  const { settings } = useSiteSettings();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      if (Array.isArray(response.data) && response.data.length > 0) {
        // Sort by created_at date descending to show newest first
        const sorted = [...response.data].sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA;
        });
        setVehicles(sorted);
      } else {
        setVehicles([]);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  // Show up to 8 vehicles on homepage
  const displayVehicles = vehicles.slice(0, 8);

  return (
    <section className="py-20 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <div className="flex items-center justify-start gap-3 mb-4">
              <span className="h-px w-8 bg-red-600"></span>
              <span className="text-red-600 font-semibold text-xs uppercase tracking-[0.22em]">{settings.inventoryTagline || "Our Inventory"}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 tracking-tight">
              {settings.inventoryTitle || "New Vehicle Arrivals"}
            </h2>
            <p className="text-gray-600 mt-4 max-w-xl text-lg">
              {vehicles.length > 0 
                ? `Browse our latest ${vehicles.length} vehicles. New inventory arriving weekly.`
                : 'New trucks, SUVs, sedans, and more arriving weekly. Find yours today.'}
            </p>
          </div>
          <Link to="/inventory">
            <Button variant="outline" className="mt-4 md:mt-0 border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white rounded-full px-7 py-3.5 font-semibold transition flex items-center gap-2 group">
              View All Inventory
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>

        {/* Vehicle grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden ring-1 ring-gray-200 shadow-sm animate-pulse">
                <div className="aspect-[4/3] bg-gray-200"></div>
                <div className="p-3 sm:p-4">
                  <div className="h-4 sm:h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-5 sm:h-8 bg-gray-200 rounded w-1/2 mb-3"></div>
                  <div className="h-3 sm:h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-8 sm:h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : displayVehicles.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {displayVehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm">
            <p className="text-gray-600">No vehicles available at the moment. Check back soon!</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default VehicleInventory;
